import { useCallback, useEffect, useState } from "react";

import { PageModule, SurveyQuestion, SurveyQuestionOption } from "../../../../../types/working-model";
import { TypographyItem } from "../../../../general-ui/typography-item/typography-item";
import classNames from 'classnames';
import SurveyOption from "./survey-option";
import { getStylingOverrides } from "../../../utils";
import { ParamsProps } from "../../../live-event";
import { useParams } from "react-router";
import { TemplateClassNames } from "../../../../../types/template-layouts";
import { useTypedSelector } from "../../../../../store/reducers/use-typed-selector";
import { CreateSurveyAnswer } from "../../../../../connection/save-survey-answer";
import { GetQuestionStats } from "../../../../../connection/surveys";
import { setCompletedSurveyQuestion } from "../../../../../store/actions/event/event-actions";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

interface IRadioSurveyQuestionProps {
	showResults: boolean;
	question: SurveyQuestion;
	module: PageModule;
	template: TemplateClassNames;
}

const RadioSurveyQuestion: React.FC<IRadioSurveyQuestionProps> = ({ showResults, question, module, template }) => {
	const [selected, setSelected] = useState(0);
	const params: ParamsProps = useParams();
	const language = params?.language;
	const { content, styling_overrides } = module;
	const stylingOverrides = getStylingOverrides(styling_overrides);
	const {
		primaryButtonOverrides,
	} = stylingOverrides;
	const [percentages, setPercentages] = useState<{ [key: number]: number }>({});
	const [totalAnswers, setTotalAnswers] = useState(0);
	const [submitDisabled, setSubmitDisabled] = useState(false);
	const [showPercentage, setShowPercentage] = useState(false);
	const { t } = useTranslation("session");

	const { event, sessions, registrationId, userSession, registration_on, surveyQuestionsCompleted, workingSession, previewMode } = useTypedSelector(state => ({
		event: state.LiveEventReducer.eventBundle?.event,
		sessions: state.LiveEventReducer.eventBundle?.sessions,
		registrationId: state.LiveEventReducer.registrationId,
		userSession: state.LiveEventReducer.userSession,
		registration_on: state.LiveEventReducer.eventBundle?.registration_on,
		surveyQuestionsCompleted: state.LiveEventReducer.surveyQuestionsCompleted,
		workingSession: state.CreateEventReducer.workingSession,
		previewMode: state.CreateEventReducer.previewMode
	}));

	const dispatch = useDispatch();

	const sessionUuid = params?.sessionUuid;
	const token = localStorage.getItem('Bl-Profile-Token');

	const getSession = useCallback(() => {
		return sessions?.find(session => session.uuid === sessionUuid);
	}, [sessions, sessionUuid]);

	const session = previewMode === 'session' ? workingSession : getSession();

	useEffect(() => {
		// if there are answers in the store, updated selected options with those answers
		if (surveyQuestionsCompleted.length) {
			for (let i = 0; i < surveyQuestionsCompleted.length; i++) {
				if (surveyQuestionsCompleted[i].question === question.survey_question) {
					setSelected(surveyQuestionsCompleted[i].option);

					// If there are options selected in the store, disable the submit button
					// and options responsivness to avoid double submissions
					setSubmitDisabled(true);
					if (showResults) {
						setShowPercentage(true);
					}
				}
			}
		}
	}, [surveyQuestionsCompleted, question.survey_question, showResults]);

	useEffect(() => {
		// This is the initial call to grab all of the submission totals for each option and set the state
		if (session) {
			const getPercentages = async () => {
				const result = await GetQuestionStats(session?.session, question.survey_question, question.question_type);
				const array: { [key: string]: number } = {};
				for (const [key, value] of Object.entries(result.counts)) {
					array[key] = value;
				}
				setPercentages(array);
				setTotalAnswers(result.total);
			};

			getPercentages();
		}
	}, [session, question]);

	const handleChange = (value: string | number) => {
		// handles selecting an option
		setSelected(value as number);
	};

	function incrementPercentages(option: number) {
		// this function increments the local state to reflect the options selected being
		// added to the values pulled from the api, without having to call the api again
		const newPercentages = percentages;
		newPercentages[option] = (1 + newPercentages[option]) || 1;
		setPercentages(newPercentages);
		setTotalAnswers(totalAnswers + 1);
	}

	const handleSubmit = (option: number) => {
		// this function handles the submission of options selected
		if (!session) return null;
		if (question.required && !selected) return;

		let body = {};
		if (registration_on) {
			// if registration for the event is turned on, submit the
			// userSession and registrationId as well
			body = {
				session: session.session,
				survey_question: question.survey_question,
				module: module.id,
				event: event,
				text_answer: null,
				radio_answer: selected,
				checkbox_answer: null,
				session_uuid: session.uuid,
				language: language,
				userSession: userSession,
				registration_id: registrationId
			};
		} else {
			body = {
				session: session.session,
				survey_question: question.survey_question,
				module: module.id,
				event: event,
				text_answer: null,
				radio_answer: selected,
				checkbox_answer: null,
				session_uuid: session.uuid,
				language: language
			};
		}
		// Send the message to microservices
		CreateSurveyAnswer(body, token);
		incrementPercentages(option);

		// update the store with the selected options
		dispatch(setCompletedSurveyQuestion(question.survey_question, option));
		setSubmitDisabled(true);
		if (showResults) {
			setShowPercentage(true);
		}
	};

	const percentage = (option: SurveyQuestionOption) => {
		// this function returns a formatted value to reprsent the percentage
		if (totalAnswers && percentages[option.survey_question_option])
			return Math.floor(percentages[option.survey_question_option] / totalAnswers * 100);
		else
			return 0;
	};

	if (!session && (previewMode !== 'session')) return null;

	return (
		<div className="question-container">
			<TypographyItem className={classNames('question-title evt-heading-4', template)} tagName="p">
				{question?.title?.[language] ?? question?.title?.base}
			</TypographyItem>
			{question.options.map(option => (
				<div className="survey-option-wrapper" key={option.survey_question_option}>
					<SurveyOption
						value={option.survey_question_option}
						title={option.label?.[language] as string ?? option.label.base}
						selected={selected === option.survey_question_option}
						onClick={handleChange}
						percentage={showPercentage ? percentage(option) : null}
						disabled={submitDisabled}
					/>
				</div>
			))}
			<div className={classNames('submit-action')}><button className={classNames('primary next', primaryButtonOverrides)} type="submit" onClick={() => handleSubmit(selected)} disabled={submitDisabled || selected === 0 || (previewMode === 'session')}>{content?.callToAction?.button_name?.[language] || t("Submit")}</button></div>
		</div>
	);
};

export default RadioSurveyQuestion;
